/** @module @liquidefy/code-editor/ai/ai-conversation @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AIConversationState,
  AIConversationId,
  AIMessage,
  AIMessageId,
  ConversationType,
  ConversationContext,
  ConversationParticipant,
  MessageContent,
  MessageType,
  MessageReaction,
  MessageAttachment,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asAIConversationId,
  asAIMessageId,
  now,
  emptyAIConversationState,
} from '../../code-editor.js';

// ═══ §2 CONVERSATION LIFECYCLE — create, close, archive, reopen ═══

/**
 * Create a new conversation with optional initial context.
 */
export function createConversation(
  id: string,
  type: ConversationType,
  initialContext?: Partial<ConversationContext>,
): AIConversationState {
  const convId = asAIConversationId(id);
  const base = emptyAIConversationState(convId);
  return {
    ...base,
    type,
    context: initialContext ? { ...base.context, ...initialContext } : base.context,
  };
}

/**
 * Close a conversation (mark as ended by setting updatedAt).
 */
export function closeConversation(state: AIConversationState): AIConversationState {
  return { ...state, updatedAt: now() };
}

/**
 * Archive a conversation (same as close, tracked by consumer).
 */
export function archiveConversation(state: AIConversationState): AIConversationState {
  return { ...state, updatedAt: now() };
}

/**
 * Reopen a conversation (refresh timestamp).
 */
export function reopenConversation(state: AIConversationState): AIConversationState {
  return { ...state, updatedAt: now() };
}

/**
 * Check if a conversation is considered active (has messages and was updated recently).
 */
export function isConversationActive(state: AIConversationState): boolean {
  // A conversation is active if it has at least one message
  // and was updated within the last hour
  if (state.messages.length === 0) return false;
  const oneHour = 3600000;
  return (now() - state.updatedAt) < oneHour;
}

// ═══ §3 MESSAGES — add, edit, delete, query ═══

function createMessage(
  authorId: string,
  role: AIMessage['role'],
  content: MessageContent,
  type: MessageType,
  parentId?: AIMessageId,
  attachments?: readonly MessageAttachment[],
): AIMessage {
  return {
    id: asAIMessageId(generateId('msg')),
    authorId,
    role,
    content,
    type,
    timestamp: now(),
    parentId,
    reactions: [],
    attachments: attachments ? [...attachments] : [],
  };
}

/**
 * Add a user message.
 */
export function addUserMessage(
  state: AIConversationState,
  text: string,
  attachments?: readonly MessageAttachment[],
): AIConversationState {
  const message = createMessage('user', 'user', { text }, 'text', undefined, attachments);
  return { ...state, messages: [...state.messages, message], updatedAt: now() };
}

/**
 * Add an assistant message with optional code.
 */
export function addAssistantMessage(
  state: AIConversationState,
  text: string,
  code?: { readonly language: string; readonly value: string },
): AIConversationState {
  const content: MessageContent = code ? { text, code } : { text };
  const type: MessageType = code ? 'code' : 'text';
  const message = createMessage('assistant', 'assistant', content, type);
  return { ...state, messages: [...state.messages, message], updatedAt: now() };
}

/**
 * Add a system message.
 */
export function addSystemMessage(state: AIConversationState, text: string): AIConversationState {
  const message = createMessage('system', 'system', { text }, 'system');
  return { ...state, messages: [...state.messages, message], updatedAt: now() };
}

/**
 * Add a tool result message.
 */
export function addToolMessage(
  state: AIConversationState,
  toolName: string,
  result: unknown,
): AIConversationState {
  const content: MessageContent = { text: `[${toolName}] ${String(result)}`, structured: result };
  const message = createMessage(toolName, 'tool', content, 'action');
  return { ...state, messages: [...state.messages, message], updatedAt: now() };
}

/**
 * Edit a message's content by ID.
 */
export function editMessage(
  state: AIConversationState,
  messageId: AIMessageId,
  content: MessageContent,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);
  const updated = [...state.messages];
  updated[idx] = { ...updated[idx]!, content };
  return ok({ ...state, messages: updated, updatedAt: now() }, 'MESSAGE_EDITED', 'Message edited');
}

/**
 * Delete a message by ID.
 */
export function deleteMessage(
  state: AIConversationState,
  messageId: AIMessageId,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);
  const updated = state.messages.filter((m) => m.id !== messageId);
  return ok({ ...state, messages: updated, updatedAt: now() }, 'MESSAGE_DELETED', 'Message deleted');
}

/**
 * Get a message by ID.
 */
export function getMessage(state: AIConversationState, messageId: AIMessageId): AIMessage | null {
  return state.messages.find((m) => m.id === messageId) ?? null;
}

/**
 * Get the last message in the conversation.
 */
export function getLastMessage(state: AIConversationState): AIMessage | null {
  if (state.messages.length === 0) return null;
  return state.messages[state.messages.length - 1] ?? null;
}

/**
 * Get the last N messages.
 */
export function getLastNMessages(state: AIConversationState, n: number): AIMessage[] {
  return state.messages.slice(Math.max(0, state.messages.length - n));
}

/**
 * Total message count.
 */
export function messageCount(state: AIConversationState): number {
  return state.messages.length;
}

// ═══ §4 THREADING — reply chains, thread traversal ═══

/**
 * Add a reply to a specific message.
 */
export function addReply(
  state: AIConversationState,
  parentId: AIMessageId,
  message: Omit<AIMessage, 'id' | 'timestamp' | 'parentId' | 'reactions' | 'attachments'>,
): EditorResult<AIConversationState> {
  const parent = state.messages.find((m) => m.id === parentId);
  if (!parent) return err('PARENT_NOT_FOUND', `Parent message ${parentId} not found`);
  const reply: AIMessage = {
    ...message,
    id: asAIMessageId(generateId('msg')),
    timestamp: now(),
    parentId,
    reactions: [],
    attachments: [],
  };
  return ok(
    { ...state, messages: [...state.messages, reply], updatedAt: now() },
    'REPLY_ADDED',
    'Reply added',
  );
}

/**
 * Get the full thread from root to a given message.
 */
export function getThread(state: AIConversationState, messageId: AIMessageId): AIMessage[] {
  const chain: AIMessage[] = [];
  let current = state.messages.find((m) => m.id === messageId);
  while (current) {
    chain.unshift(current);
    if (!current.parentId) break;
    current = state.messages.find((m) => m.id === current!.parentId);
  }
  return chain;
}

/**
 * Get direct replies to a message.
 */
export function getReplies(state: AIConversationState, messageId: AIMessageId): AIMessage[] {
  return state.messages.filter((m) => m.parentId === messageId);
}

/**
 * Flatten all messages in a thread starting from a root message (depth-first).
 */
export function flattenThread(state: AIConversationState, messageId: AIMessageId): AIMessage[] {
  const result: AIMessage[] = [];
  const root = state.messages.find((m) => m.id === messageId);
  if (!root) return result;

  const stack: AIMessage[] = [root];
  while (stack.length > 0) {
    const msg = stack.pop()!;
    result.push(msg);
    const replies = getReplies(state, msg.id);
    // Push in reverse so first reply is processed first
    for (let i = replies.length - 1; i >= 0; i--) {
      stack.push(replies[i]!);
    }
  }
  return result;
}

/**
 * Compute thread depth (0 for root, 1 for direct reply, etc.).
 */
export function threadDepth(state: AIConversationState, messageId: AIMessageId): number {
  let depth = 0;
  let current = state.messages.find((m) => m.id === messageId);
  while (current?.parentId) {
    depth++;
    current = state.messages.find((m) => m.id === current!.parentId);
  }
  return depth;
}

/**
 * Find the root message of a thread.
 */
export function rootMessage(state: AIConversationState, messageId: AIMessageId): AIMessage | null {
  let current = state.messages.find((m) => m.id === messageId);
  if (!current) return null;
  while (current.parentId) {
    const parent = state.messages.find((m) => m.id === current!.parentId);
    if (!parent) break;
    current = parent;
  }
  return current;
}

// ═══ §5 REACTIONS & ATTACHMENTS — emojis, files ═══

/**
 * Add a reaction to a message.
 */
export function addReaction(
  state: AIConversationState,
  messageId: AIMessageId,
  emoji: string,
  userId: string,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);

  const msg = state.messages[idx]!;
  // Prevent duplicate reactions from same user with same emoji
  if (msg.reactions.some((r) => r.emoji === emoji && r.userId === userId)) {
    return ok(state, 'REACTION_EXISTS', 'Reaction already exists');
  }

  const reaction: MessageReaction = { emoji, userId, timestamp: now() };
  const updated = [...state.messages];
  updated[idx] = { ...msg, reactions: [...msg.reactions, reaction] } as AIMessage;
  return ok({ ...state, messages: updated, updatedAt: now() }, 'REACTION_ADDED', 'Reaction added');
}

/**
 * Remove a reaction from a message.
 */
export function removeReaction(
  state: AIConversationState,
  messageId: AIMessageId,
  emoji: string,
  userId: string,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);

  const msg = state.messages[idx]!;
  const filtered = msg.reactions.filter((r) => !(r.emoji === emoji && r.userId === userId));
  if (filtered.length === msg.reactions.length) {
    return ok(state, 'REACTION_NOT_FOUND', 'Reaction not found');
  }

  const updated = [...state.messages];
  updated[idx] = { ...msg, reactions: filtered } as AIMessage;
  return ok({ ...state, messages: updated, updatedAt: now() }, 'REACTION_REMOVED', 'Reaction removed');
}

/**
 * Add an attachment to a message.
 */
export function addAttachment(
  state: AIConversationState,
  messageId: AIMessageId,
  attachment: MessageAttachment,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);

  const msg = state.messages[idx]!;
  const updated = [...state.messages];
  updated[idx] = { ...msg, attachments: [...msg.attachments, attachment] } as AIMessage;
  return ok({ ...state, messages: updated, updatedAt: now() }, 'ATTACHMENT_ADDED', 'Attachment added');
}

/**
 * Remove an attachment from a message by attachment ID.
 */
export function removeAttachment(
  state: AIConversationState,
  messageId: AIMessageId,
  attachmentId: string,
): EditorResult<AIConversationState> {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return err('MESSAGE_NOT_FOUND', `Message ${messageId} not found`);

  const msg = state.messages[idx]!;
  const filtered = msg.attachments.filter((a) => a.id !== attachmentId);
  const updated = [...state.messages];
  updated[idx] = { ...msg, attachments: filtered } as AIMessage;
  return ok({ ...state, messages: updated, updatedAt: now() }, 'ATTACHMENT_REMOVED', 'Attachment removed');
}

/**
 * Get all attachments across all messages matching a MIME type prefix.
 */
export function attachmentsByType(state: AIConversationState, mimeType: string): MessageAttachment[] {
  const result: MessageAttachment[] = [];
  for (const msg of state.messages) {
    for (const att of msg.attachments) {
      if (att.mimeType.startsWith(mimeType)) {
        result.push(att);
      }
    }
  }
  return result;
}

// ═══ §6 CONTEXT MANAGEMENT — update, estimate tokens, truncate ═══

/**
 * Update the conversation context.
 */
export function updateContext(
  state: AIConversationState,
  context: Partial<ConversationContext>,
): AIConversationState {
  return {
    ...state,
    context: { ...state.context, ...context },
    updatedAt: now(),
  };
}

/**
 * Compute the context at a specific message point (context + messages up to that point).
 */
export function contextForMessage(
  state: AIConversationState,
  messageId: AIMessageId,
): ConversationContext {
  const idx = state.messages.findIndex((m) => m.id === messageId);
  if (idx === -1) return state.context;

  // Collect recent commands from all messages up to this point
  const commands: string[] = [];
  for (let i = 0; i <= idx; i++) {
    const msg = state.messages[i]!;
    if (msg.type === 'action' && msg.content.text) {
      commands.push(msg.content.text);
    }
  }

  return {
    ...state.context,
    recentCommands: commands.length > 0 ? commands : state.context.recentCommands,
  };
}

/**
 * Estimate total tokens used by the conversation.
 * Rough heuristic: ~4 characters per token.
 */
export function tokensUsed(state: AIConversationState): number {
  let totalChars = 0;
  for (const msg of state.messages) {
    if (msg.content.text) totalChars += msg.content.text.length;
    if (msg.content.code) totalChars += msg.content.code.value.length;
    if (msg.content.structured) {
      try {
        totalChars += JSON.stringify(msg.content.structured).length;
      } catch {
        totalChars += 100;
      }
    }
  }
  return Math.ceil(totalChars / 4);
}

/**
 * Truncate conversation to fit within a token budget.
 * Keeps the most recent messages and system messages.
 */
export function truncateToFit(
  state: AIConversationState,
  maxTokens: number,
): AIConversationState {
  const current = tokensUsed(state);
  if (current <= maxTokens) return state;

  // Keep system messages and recent messages
  const systemMessages = state.messages.filter((m) => m.role === 'system');
  const nonSystem = state.messages.filter((m) => m.role !== 'system');

  // Estimate tokens for system messages
  let sysTokens = 0;
  for (const msg of systemMessages) {
    sysTokens += Math.ceil((msg.content.text?.length ?? 0) / 4);
  }

  const remainingBudget = maxTokens - sysTokens;
  if (remainingBudget <= 0) {
    return { ...state, messages: systemMessages.slice(-1), updatedAt: now() };
  }

  // Work backwards from most recent
  const kept: AIMessage[] = [];
  let used = 0;
  for (let i = nonSystem.length - 1; i >= 0; i--) {
    const msg = nonSystem[i]!;
    const msgTokens = Math.ceil((msg.content.text?.length ?? 0) / 4) + Math.ceil((msg.content.code?.value.length ?? 0) / 4);
    if (used + msgTokens > remainingBudget) break;
    kept.unshift(msg);
    used += msgTokens;
  }

  const finalMessages = [...systemMessages, ...kept];
  return { ...state, messages: finalMessages, updatedAt: now() };
}

/**
 * Summarize the conversation into a brief text of key points.
 */
export function summarizeConversation(state: AIConversationState): string {
  if (state.messages.length === 0) return 'Empty conversation.';

  const parts: string[] = [];
  parts.push(`Conversation type: ${state.type}`);
  parts.push(`Messages: ${state.messages.length}`);

  const roles = new Map<string, number>();
  for (const msg of state.messages) {
    roles.set(msg.role, (roles.get(msg.role) ?? 0) + 1);
  }
  const roleStr = Array.from(roles.entries()).map(([r, c]) => `${r}(${c})`).join(', ');
  parts.push(`Participants: ${roleStr}`);

  // Key topics: extract first words of user messages
  const userMsgs = state.messages.filter((m) => m.role === 'user' && m.content.text);
  if (userMsgs.length > 0) {
    const topics = userMsgs.slice(-3).map((m) => {
      const text = m.content.text ?? '';
      return text.length > 60 ? text.slice(0, 60) + '...' : text;
    });
    parts.push(`Recent topics: ${topics.join('; ')}`);
  }

  const hasCode = state.messages.some((m) => m.content.code);
  if (hasCode) parts.push('Contains code blocks');

  return parts.join('\n');
}

/**
 * Format the conversation as a prompt suitable for an AI model.
 */
export function conversationToPrompt(state: AIConversationState): string {
  const lines: string[] = [];

  if (state.context.activeFile) {
    lines.push(`[Active file: ${state.context.activeFile}]`);
  }
  if (state.context.selectedCode) {
    lines.push(`[Selected code: ${state.context.selectedCode.slice(0, 200)}]`);
  }

  for (const msg of state.messages) {
    const roleLabel = msg.role.charAt(0).toUpperCase() + msg.role.slice(1);
    if (msg.content.text) {
      lines.push(`${roleLabel}: ${msg.content.text}`);
    }
    if (msg.content.code) {
      lines.push(`\`\`\`${msg.content.code.language}\n${msg.content.code.value}\n\`\`\``);
    }
  }

  return lines.join('\n\n');
}

// ═══ §7 CONVERSATION SEARCH — filter and find messages ═══

/**
 * Search messages by text content (case-insensitive substring match).
 */
export function searchMessages(state: AIConversationState, query: string): AIMessage[] {
  const lower = query.toLowerCase();
  return state.messages.filter((m) => {
    const text = (m.content.text ?? '').toLowerCase();
    const code = (m.content.code?.value ?? '').toLowerCase();
    return text.includes(lower) || code.includes(lower);
  });
}

/**
 * Filter messages by role.
 */
export function filterByRole(state: AIConversationState, role: AIMessage['role']): AIMessage[] {
  return state.messages.filter((m) => m.role === role);
}

/**
 * Filter messages by type.
 */
export function filterByType(state: AIConversationState, type: MessageType): AIMessage[] {
  return state.messages.filter((m) => m.type === type);
}

/**
 * Get messages within a time range (inclusive).
 */
export function messagesInTimeRange(
  state: AIConversationState,
  start: number,
  end: number,
): AIMessage[] {
  return state.messages.filter((m) => m.timestamp >= start && m.timestamp <= end);
}

/**
 * Extract all code blocks from the entire conversation.
 */
export function codeBlocksFromConversation(
  state: AIConversationState,
): Array<{ readonly language: string; readonly value: string; readonly messageId: AIMessageId }> {
  const blocks: Array<{ readonly language: string; readonly value: string; readonly messageId: AIMessageId }> = [];
  for (const msg of state.messages) {
    if (msg.content.code) {
      blocks.push({
        language: msg.content.code.language,
        value: msg.content.code.value,
        messageId: msg.id,
      });
    }
    // Also extract fenced code blocks from text
    if (msg.content.text) {
      const regex = /```(\w*)\n([\s\S]*?)```/g;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(msg.content.text)) !== null) {
        blocks.push({
          language: match[1] || 'text',
          value: match[2]!.trim(),
          messageId: msg.id,
        });
      }
    }
  }
  return blocks;
}
